import { useEffect, useRef, useState } from 'react';
import { ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { routes } from '../data/site';
import { SiteLink } from './SiteLink';

/**
 * Reviews are intentionally empty until a live Google review feed is connected.
 * When `reviews` is supplied, the carousel below renders in place of the
 * support sentence without any other change to this section.
 */
export type GoogleReview = {
  id: string;
  author: string;
  rating: number;
  date: string;
  text: string;
};
type ReviewsCalloutProps = {
  reviews?: GoogleReview[];
  rotateMs?: number;
};
export function ReviewsCallout({
  reviews = [],
  rotateMs = 7000
}: ReviewsCalloutProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const regionRef = useRef<HTMLDivElement>(null);
  const hasFeed = reviews.length > 0;
  useEffect(() => {
    if (!hasFeed || paused || reviews.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = window.setInterval(() => {
      setIndex(current => (current + 1) % reviews.length);
    }, rotateMs);
    return () => window.clearInterval(timer);
  }, [hasFeed, paused, reviews.length, rotateMs]);
  return <div className="mt-12 border-t border-ink/10 pt-6" data-id="element-352" data-mp="2jf18abnioxz6">
      <div ref={regionRef} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between" data-id="element-353" data-mp="2emq6f397v23z">
        <div className="min-w-0" data-id="element-354" data-mp="32cucdh9vndy5">
          <h3 className="text-[20px] font-semibold leading-7 text-ink" data-id="element-355" data-mp="32bsyz65e2fd9">What Local Customers Say</h3>
          {hasFeed ? <p className="mt-1 max-w-2xl text-body text-ink/80" aria-live="polite" data-id="element-356" data-mp="2xbn47h3tocqy">
              {reviews[index].text}
            </p> : <p className="mt-1 text-body text-ink/80" data-id="element-357" data-mp="130rugbgwirml">
              Read feedback from our customers on Google.
            </p>}
        </div>

        <div className="flex shrink-0 items-center gap-2" data-id="element-358" data-mp="1wp7fylmu0yf8">
          {hasFeed && reviews.length > 1 && <>
              <button type="button" aria-label="Previous review" onClick={() => setIndex(current => (current - 1 + reviews.length) % reviews.length)} className="flex h-11 w-11 items-center justify-center rounded-btn border border-ink/20 text-ink transition-colors duration-150 ease-out hover:bg-water" data-id="element-359" data-mp="21o0tge24cpfe">
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-360" data-mp="2kg3335gcf5g5" />
              </button>
              <button type="button" aria-label="Next review" onClick={() => setIndex(current => (current + 1) % reviews.length)} className="flex h-11 w-11 items-center justify-center rounded-btn border border-ink/20 text-ink transition-colors duration-150 ease-out hover:bg-water" data-id="element-361" data-mp="2pctjxuzz252m">
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-362" data-mp="ipvtc0hoyytf" />
              </button>
            </>}
          <SiteLink to={routes.reviews} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-btn border border-pegasus-blue px-4 text-[16px] font-bold text-pegasus-blue transition-colors duration-150 ease-out hover:bg-pegasus-blue hover:text-white" data-id="element-363" data-mp="dpq1aj7ifaat">
            Read Our Google Reviews
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-364" data-mp="11gvpblnlt8w7" />
          </SiteLink>
        </div>
      </div>
    </div>;
}